/* oxlint-disable typescript/no-unsafe-assignment, typescript/no-unsafe-call, typescript/no-unsafe-member-access, typescript/no-unsafe-argument, typescript/no-unsafe-return, typescript/no-unnecessary-type-parameters */
import { useCallback, useEffect, useState } from 'react'
import type {
  DefaultSchema,
  ObjectSchema,
  OptionalSchema,
  Schema,
  Shape,
  output,
} from '@w/schema'
import { createEvent } from '../create-event'
import { localStorageOrMemory, useLocalStorageContext } from '../local-storage-ctx'

type InferStorageValue<T> =
  T extends DefaultSchema<infer S>
    ? output<S>
    : T extends OptionalSchema<infer S>
      ? output<S> | undefined
      : never

type StorageKeys<T extends Shape> = {
  [K in keyof T]: T[K] extends DefaultSchema<Schema> | OptionalSchema<Schema>
    ? K
    : never
}[keyof T]

interface TypedLocalStorageOptions {
  prefix?: string
}

interface TypedLocalStorage<T extends Shape> {
  get<K extends StorageKeys<T>>(key: K): InferStorageValue<T[K]>
  set<K extends StorageKeys<T>>(key: K, value: InferStorageValue<T[K]>): void
  delete<K extends StorageKeys<T>>(key: K): void
  useLocalStorage<K extends StorageKeys<T>>(
    key: K,
  ): [
    InferStorageValue<T[K]>,
    (
      value:
        | InferStorageValue<T[K]>
        | ((prev: InferStorageValue<T[K]>) => InferStorageValue<T[K]>),
    ) => void,
  ]
}

type SyncEvent = { key: string; value: unknown }

export function makeTypedLocalStorage<T extends Shape>(
  schema: ObjectSchema<T>,
  options: TypedLocalStorageOptions = {},
): TypedLocalStorage<T> {
  const { prefix } = options
  const shape = schema.shape

  const syncEvent = createEvent<SyncEvent>('typed-local-storage-sync')

  function getStorageKey(key: string): string {
    return prefix ? `${prefix}:${key}` : key
  }

  function getDefaultValue<K extends keyof T>(key: K): InferStorageValue<T[K]> {
    const fieldSchema = shape[key]
    const def = fieldSchema._zod.def
    if (def.type === 'default') {
      const defaultValue = def.defaultValue
      return typeof defaultValue === 'function' ? defaultValue() : defaultValue
    }
    return undefined as InferStorageValue<T[K]>
  }

  function get<K extends StorageKeys<T>>(
    key: K,
    storage: Storage = localStorageOrMemory(),
  ): InferStorageValue<T[K]> {
    const storageKey = getStorageKey(key as string)
    try {
      const item = storage.getItem(storageKey)
      if (item === null) return getDefaultValue(key)
      const parsed = JSON.parse(item)
      const fieldSchema = shape[key]
      const result = fieldSchema.safeParse(parsed)
      if (result.success) return result.data as InferStorageValue<T[K]>
      return getDefaultValue(key)
    } catch {
      return getDefaultValue(key)
    }
  }

  function set<K extends StorageKeys<T>>(
    key: K,
    value: InferStorageValue<T[K]>,
    storage: Storage = localStorageOrMemory(),
  ): void {
    const storageKey = getStorageKey(key as string)
    if (value === undefined) {
      storage.removeItem(storageKey)
    } else {
      storage.setItem(storageKey, JSON.stringify(value))
    }
    syncEvent.emit({ key: key as string, value })
  }

  function del<K extends StorageKeys<T>>(
    key: K,
    storage: Storage = localStorageOrMemory(),
  ): void {
    const storageKey = getStorageKey(key as string)
    storage.removeItem(storageKey)
    syncEvent.emit({ key: key as string, value: getDefaultValue(key) })
  }

  function useLocalStorage<K extends StorageKeys<T>>(
    key: K,
  ): [
    InferStorageValue<T[K]>,
    (
      value:
        | InferStorageValue<T[K]>
        | ((prev: InferStorageValue<T[K]>) => InferStorageValue<T[K]>),
    ) => void,
  ] {
    const storage = useLocalStorageContext()
    const [value, setValueState] = useState<InferStorageValue<T[K]>>(() =>
      get(key, storage),
    )

    const setValue = useCallback(
      (
        newValue:
          | InferStorageValue<T[K]>
          | ((prev: InferStorageValue<T[K]>) => InferStorageValue<T[K]>),
      ) => {
        setValueState((prev) => {
          const resolved =
            typeof newValue === 'function'
              ? (
                  newValue as (
                    prev: InferStorageValue<T[K]>,
                  ) => InferStorageValue<T[K]>
                )(prev)
              : newValue
          set(key, resolved, storage)
          return resolved
        })
      },
      [key, storage],
    )

    // Sync across instances in same tab
    syncEvent.useListen((event) => {
      if (event.key === key) {
        setValueState(event.value as InferStorageValue<T[K]>)
      }
    })

    // Sync across tabs
    useEffect(() => {
      const storageKey = getStorageKey(key as string)
      const handleStorageChange = (e: StorageEvent) => {
        if (e.key === storageKey && e.storageArea === storage) {
          if (e.newValue === null) {
            setValueState(getDefaultValue(key))
          } else {
            try {
              const parsed = JSON.parse(e.newValue)
              const fieldSchema = shape[key]
              const result = fieldSchema.safeParse(parsed)
              if (result.success) {
                setValueState(result.data as InferStorageValue<T[K]>)
              } else {
                setValueState(getDefaultValue(key))
              }
            } catch {
              setValueState(getDefaultValue(key))
            }
          }
        }
      }
      window.addEventListener('storage', handleStorageChange)
      return () => {
        window.removeEventListener('storage', handleStorageChange)
      }
    }, [key, storage])

    return [value, setValue]
  }

  return { get, set, delete: del, useLocalStorage }
}
